// biome-ignore lint/suspicious/noReactSpecificProps: <explanation>
/** biome-ignore-all lint/a11y/useKeyWithClickEvents: <explanation> */
/** biome-ignore-all lint/a11y/noStaticElementInteractions: <explanation> */
// biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
import { usePrivy } from "@privy-io/react-auth";
import {
	Calendar,
	CheckCircle2,
	Clock,
	FileText,
	Link2,
	Image as LucideImage,
	Share2,
	Star,
	X,
} from "lucide-react";
import {
	type MouseEvent,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { useShinkaiIdentityQuery } from "@/hooks/shinkai-identity";
import { cn } from "@/lib/utils";
import { Button, buttonVariants } from "./ui/button";

// --- Types ---

export type QuestLifecycle = "active" | "upcoming" | "expired";

export type QuestPublishStatus = "draft" | "active" | "archived";

export type QuestUserState =
	| "not_started"
	| "in_progress"
	| "completed"
	| "maxed_out";

export type RewardsQuestStep = {
	id: string;
	questId: string;
	name: string;
	description: string | null;
	eventName: string | null;
	requiredCount: number;
	withinSeconds: number | null;
};

export type RewardsQuest = {
	id: string;
	name: string;
	description: string | null;
	imageUrl: string | null;
	status: string;
	points: number;
	startAt: string | null;
	endAt: string | null;
	maxCompletionsPerUser: number | null;
	createdAt: string;
	updatedAt: string;
};

export type QuestWithSteps = {
	quest: RewardsQuest;
	steps: RewardsQuestStep[];
	lifecycle?: QuestLifecycle;
	publishStatus?: QuestPublishStatus;
};

export type QuestProgress = {
	completedSteps: number;
	totalSteps: number;
	status: "not_started" | "in_progress" | "completed" | "claimed" | "expired";
	completedStepIds?: string[];
};

/** Per-quest summary returned by the unified my-quests endpoint. */
export type MyQuestSummary = {
	quest: RewardsQuest;
	steps?: RewardsQuestStep[];
	progress: {
		id: string;
		userId: string;
		questId: string;
		status: string;
		startedAt: string;
		completedAt: string | null;
		updatedAt: string;
	} | null;
	totalSteps: number;
	completedSteps: number;
	completedStepIds?: string[];
	publishStatus: QuestPublishStatus;
	lifecycle: QuestLifecycle;
	userState: QuestUserState;
	canProgressNow: boolean;
	completionCount: number;
	earnedPoints: number;
};

export type RewardsPointsSummary = {
	totalPoints: number;
	questPoints: number;
	grantPoints: number;
};

/** Response shape for GET /api/rewards/my-quests. */
export type MyQuestsResponse = {
	quests: MyQuestSummary[];
	pointsSummary?: RewardsPointsSummary;
	filterCounts?: Record<string, number>;
	appliedFilters?: Record<string, string>;
};

// --- Countdown Hook ---

function useCountdown(targetDate: Date | null) {
	const [timeLeft, setTimeLeft] = useState<string | null>(null);

	useEffect(() => {
		if (!targetDate) {
			setTimeLeft(null);
			return;
		}

		const update = () => {
			const now = new Date();
			const diff = targetDate.getTime() - now.getTime();
			if (diff <= 0) {
				setTimeLeft(null);
				return;
			}

			const days = Math.floor(diff / (1000 * 60 * 60 * 24));
			const hours = Math.floor(
				(diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
			);
			const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

			if (days > 7) {
				setTimeLeft(`${days}d left`);
			} else if (days > 0) {
				setTimeLeft(`${days}d ${hours}h left`);
			} else if (hours > 0) {
				setTimeLeft(`${hours}h ${minutes}m left`);
			} else {
				setTimeLeft(`${minutes}m left`);
			}
		};

		update();
		// Update every minute for efficiency
		const interval = setInterval(update, 60_000);
		return () => clearInterval(interval);
	}, [targetDate]);

	return timeLeft;
}

type QuestCardProps = QuestWithSteps & {
	progress?: QuestProgress;
	userState?: QuestUserState;
	/** Server-provided flag — false for archived/draft quests. */
	canProgressNow?: boolean;
};

export default function QuestCard({
	quest,
	steps,
	progress,
	lifecycle: lifecycleProp,
	userState,
	canProgressNow,
}: QuestCardProps) {
	const { authenticated, ready } = usePrivy();
	const navigate = useNavigate();
	const { data: identityData } = useShinkaiIdentityQuery();

	const stepCount = steps.length;

	// Derive lifecycle from prop or date math
	const lifecycle = useMemo(() => {
		if (lifecycleProp) return lifecycleProp;
		const now = new Date();
		const end = quest.endAt ? new Date(quest.endAt) : null;
		if (end && end < now) return "expired" as const;
		return "active" as const;
	}, [lifecycleProp, quest.endAt]);

	const isEnded = lifecycle === "expired";

	const shouldShowRewardsGate =
		authenticated && ready && identityData?.rewards === false;

	// Prefer userState from backend when available
	const effectiveUserState: QuestUserState | undefined =
		userState ?? (progress ? (progress.status as QuestUserState) : undefined);

	// Calculate progress percentage
	const hasProgress =
		progress !== undefined || effectiveUserState !== undefined;
	const progressPercent = progress?.totalSteps
		? Math.min(
				100,
				Math.round((progress.completedSteps / progress.totalSteps) * 100),
			)
		: effectiveUserState === "completed" || effectiveUserState === "maxed_out"
			? 100
			: 0;
	const isQuestCompleted =
		effectiveUserState === "completed" ||
		effectiveUserState === "maxed_out" ||
		progress?.status === "completed" ||
		progress?.status === "claimed";

	const handleQuestCtaClick = (event: MouseEvent<HTMLAnchorElement>) => {
		if (!shouldShowRewardsGate) return;
		event.preventDefault();
		navigate("/profile");
	};

	// Countdown timer for active quests
	const endAtDate = useMemo(
		() => (quest.endAt ? new Date(quest.endAt) : null),
		[quest.endAt],
	);
	const countdownText = useCountdown(isEnded ? null : endAtDate);

	const [searchParams, setSearchParams] = useSearchParams();
	const detailsOpen = searchParams.get("quest") === quest.id;

	const setDetailsOpen = useCallback(
		(open: boolean) => {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					if (open) {
						next.set("quest", quest.id);
					} else {
						next.delete("quest");
					}
					return next;
				},
				{ replace: true },
			);
		},
		[quest.id, setSearchParams],
	);

	// Formatted end date (e.g. "Oct 20, 2:00 PM")
	const endDateFormatted = useMemo(() => {
		if (!endAtDate || isEnded) return null;
		return endAtDate.toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
			hour: "numeric",
			minute: "2-digit",
		});
	}, [endAtDate, isEnded]);

	return (
		<>
			<div
				onClick={() => setDetailsOpen(true)}
				className={cn(
					"group relative flex flex-col w-full rounded-[18px] p-2.5 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl cursor-pointer",
					isQuestCompleted
						? "bg-linear-to-br from-success/15 to-success/5 shadow-success/10 border border-success/30"
						: "bg-[#121214] border border-[#27272a] shadow-xl hover:shadow-brand/5",
				)}
			>
				{/* Card Frame */}
				<div
					className={cn(
						"relative flex flex-col h-full w-full rounded-[12px] overflow-hidden bg-[#18181b] border border-white/5",
						isQuestCompleted && "ring-1 ring-success/30",
					)}
				>
					{/* Noise Texture */}
					<div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.03] pointer-events-none z-0"></div>

					{/* --- Header Bar --- */}
					<div className="relative z-10 flex items-center justify-between gap-2 min-h-11 px-3.5 py-2 bg-[#202023] border-b border-white/5">
						<div className="flex items-start gap-2 min-w-0 flex-1">
							<h4
								className="font-medium text-sm text-text-default line-clamp-2 ml-1 leading-snug"
								title={quest.name}
							>
								{quest.name}
							</h4>
						</div>
						{/* Points Badge — prominent */}
						<div className="relative flex items-center gap-1 h-8 px-2.5 rounded-lg border border-brand/20  shrink-0">
							<Star className="w-3.5 h-3.5 fill-brand text-brand" />
							<span className="text-sm font-extrabold text-brand tabular-nums">
								{quest.points}
							</span>
							<span className="text-[9px] font-bold text-text-secondary uppercase ml-0.5">
								pts
							</span>
						</div>
					</div>

					{/* --- Card Art (1:1 Aspect Ratio) --- */}
					<div className="relative w-full aspect-square bg-black/40 overflow-hidden border-b border-white/5 group-hover:brightness-110 transition-all duration-500">
						{quest.imageUrl ? (
							<img
								src={quest.imageUrl}
								alt={quest.name}
								className={cn(
									"w-full h-full object-cover transition-transform duration-700 group-hover:scale-105",
									isEnded && "grayscale opacity-50",
								)}
							/>
						) : (
							<div className="w-full h-full flex flex-col items-center justify-center bg-linear-to-br from-[#202023] to-[#18181b]">
								<LucideImage className="w-12 h-12 text-text-tertiary/20 mb-2" />
								<span className="text-xs text-text-tertiary/40 uppercase tracking-widest font-bold">
									Shinkai Quest
								</span>
							</div>
						)}

						{/* Progress Bar (Overlay at bottom of Art) */}
						{hasProgress && progressPercent > 0 && !isQuestCompleted && (
							<div className="absolute bottom-0 left-0 right-0 h-1 bg-black/50 z-20">
								<div
									className="h-full bg-brand transition-all duration-500"
									style={{ width: `${progressPercent}%` }}
								/>
							</div>
						)}

						{/* Status Overlay Badge (Top Left of Art) */}
						{isQuestCompleted && (
							<div className="absolute top-2 left-2 z-20">
								<div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-success/90 backdrop-blur-sm border border-success/20 shadow-lg">
									<CheckCircle2 className="w-3.5 h-3.5 text-white" />
									<span className="text-[10px] font-bold text-white uppercase tracking-wider">
										Completed
									</span>
								</div>
							</div>
						)}
						{!isQuestCompleted && isEnded && (
							<div className="absolute top-2 left-2 z-20">
								<div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-bg-tertiary/90 backdrop-blur-sm border border-white/10 shadow-lg">
									<Clock className="w-3.5 h-3.5 text-text-tertiary" />
									<span className="text-[10px] font-bold text-text-tertiary uppercase tracking-wider">
										Expired
									</span>
								</div>
							</div>
						)}

						{/* Deadline Overlay (Bottom Right of Art) */}
						{!isQuestCompleted &&
							!isEnded &&
							(countdownText || endDateFormatted) && (
								<div className="absolute bottom-2 right-2 z-20">
									<div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-black/70 backdrop-blur-sm border border-white/10">
										<Clock className="w-3.5 h-3.5 text-amber-400" />
										<div className="flex flex-col">
											{countdownText && (
												<span className="text-[10px] font-bold text-amber-300">
													{countdownText}
												</span>
											)}
											{endDateFormatted && (
												<span className="text-[9px] font-medium text-text-tertiary">
													Ends {endDateFormatted}
												</span>
											)}
										</div>
									</div>
								</div>
							)}
					</div>

					{/* --- Card Body (Text Box) --- */}
					<div className="relative flex-1 flex flex-col p-4 z-10">
						{/* Type Line */}
						<div className="flex items-center gap-2 mb-3 pb-2">
							<span className="text-[10px] uppercase tracking-widest font-bold text-text-tertiary">
								Challenge
							</span>
							<div className="h-px flex-1 bg-white/5" />
						</div>

						{/* Description */}
						<div className="flex gap-2 mb-4 pb-2">
							<div className="mt-0.5 shrink-0 opacity-50">
								<FileText className="w-3.5 h-3.5 text-text-tertiary" />
							</div>
							<p className="text-xs text-text-secondary leading-relaxed line-clamp-2">
								{quest.description ??
									"Complete this quest to earn rewards points and collect this card."}
							</p>
						</div>

						{/* Footer / CTA (Visual only, clicking card opens modal) */}
						<div className="mt-auto pt-2">
							{isQuestCompleted ? (
								<div className="w-full py-2 text-center text-xs font-bold text-success uppercase tracking-widest opacity-90">
									Card Added to Collection
								</div>
							) : isEnded ? (
								<div className="w-full text-center text-xs text-text-tertiary italic">
									Challenge Expired
								</div>
							) : canProgressNow === false ? (
								<div className="w-full flex items-center justify-center gap-2 py-2 rounded bg-white/5 border border-white/5 text-text-tertiary">
									<Clock className="w-3.5 h-3.5" />
									<span className="text-xs font-medium">Coming Soon</span>
								</div>
							) : (
								<div
									className={cn(
										buttonVariants({ variant: "outline", size: "xs" }),
										"w-full h-8 text-xs uppercase tracking-wide font-bold bg-[#202023] hover:bg-[#2a2a2d] border-white/10 hover:border-brand/30 hover:text-brand transition-all flex items-center justify-center pointer-events-none",
									)}
								>
									{shouldShowRewardsGate
										? "Unlock Rewards"
										: hasProgress && (progress?.completedSteps ?? 0) > 0
											? "Resume Challenge"
											: "Start Challenge"}
								</div>
							)}
						</div>
					</div>
				</div>
			</div>

			<QuestDetailsDialog
				open={detailsOpen}
				onOpenChange={setDetailsOpen}
				quest={quest}
				steps={steps}
				progress={progress}
				lifecycle={lifecycle}
				userState={effectiveUserState}
				canProgressNow={canProgressNow}
				progressPercent={progressPercent}
				isQuestCompleted={isQuestCompleted}
				isEnded={isEnded}
				shouldShowRewardsGate={shouldShowRewardsGate ?? false}
				hasProgress={hasProgress}
				onCtaClick={handleQuestCtaClick}
				countdownText={countdownText}
				endDateFormatted={endDateFormatted}
			/>
		</>
	);
}

function CopyLinkButton({ onClick }: { onClick: () => void }) {
	const [copied, setCopied] = useState(false);

	const handleClick = () => {
		onClick();
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	return (
		<Button
			size="icon"
			variant="outline"
			className="h-8 w-8 rounded-full bg-black/50 hover:bg-black/70 border border-white/10 backdrop-blur-md"
			onClick={handleClick}
			title="Copy link"
		>
			{copied ? (
				<CheckCircle2 className="w-4 h-4 text-green-400" />
			) : (
				<Link2 className="w-4 h-4 text-white" />
			)}
		</Button>
	);
}

function QuestDetailsDialog({
	open,
	onOpenChange,
	quest,
	steps,
	progress,
	lifecycle,
	userState,
	canProgressNow,
	progressPercent,
	isQuestCompleted,
	isEnded,
	shouldShowRewardsGate,
	hasProgress,
	onCtaClick,
	countdownText,
	endDateFormatted,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	quest: RewardsQuest;
	steps: RewardsQuestStep[];
	progress?: QuestProgress;
	lifecycle: QuestLifecycle;
	userState?: QuestUserState;
	canProgressNow?: boolean;
	progressPercent: number;
	isQuestCompleted: boolean;
	isEnded: boolean;
	shouldShowRewardsGate: boolean;
	hasProgress: boolean;
	onCtaClick: (e: MouseEvent<HTMLAnchorElement>) => void;
	countdownText: string | null;
	endDateFormatted: string | null;
}) {
	const shareUrl = useMemo(() => {
		const base = typeof window !== "undefined" ? window.location.origin : "";
		return `${base}/quests?quest=${quest.id}`;
	}, [quest.id]);

	const shareText = isQuestCompleted
		? `I just completed the "${quest.name}" challenge on Shinkai and earned ${quest.points} points!`
		: `Check out the "${quest.name}" challenge on Shinkai! Earn ${quest.points} points.`;

	const handleShareX = () => {
		const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
			shareText,
		)}&url=${encodeURIComponent(shareUrl)}`;
		window.open(url, "_blank");
	};

	const handleCopyLink = async () => {
		try {
			await navigator.clipboard.writeText(shareUrl);
		} catch {
			// Fallback: noop
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-h-[90vh] max-w-2xl p-0 gap-0 overflow-y-auto overflow-x-hidden border-[#27272a] bg-[#121214]">
				{/* Header Image Area */}
				<div className="relative w-full aspect-video bg-black/40 overflow-hidden border-b border-white/5">
					{quest.imageUrl ? (
						<img
							src={quest.imageUrl}
							alt={quest.name}
							className={cn(
								"w-full h-full object-cover",
								isEnded && "grayscale opacity-50",
							)}
						/>
					) : (
						<div className="w-full h-full flex flex-col items-center justify-center bg-linear-to-br from-[#202023] to-[#18181b]">
							<LucideImage className="w-16 h-16 text-text-tertiary/20 mb-2" />
						</div>
					)}

					<div className="absolute top-4 right-4 z-20 flex gap-2">
						<Button
							size="icon"
							variant="outline"
							className="h-8 w-8 rounded-full bg-black/50 hover:bg-black/70 border border-white/10 backdrop-blur-md"
							onClick={handleShareX}
							title="Share on X"
						>
							<Share2 className="w-4 h-4 text-white" />
						</Button>
						<CopyLinkButton onClick={handleCopyLink} />
						<Button
							size="icon"
							variant="outline"
							className="h-8 w-8 rounded-full bg-black/50 hover:bg-black/70 border border-white/10 backdrop-blur-md"
							onClick={() => onOpenChange(false)}
						>
							<X className="w-4 h-4 text-white" />
						</Button>
					</div>

					{/* Status Badge Over Image */}
					<div className="absolute bottom-4 left-4 flex gap-2">
						{isQuestCompleted ? (
							<div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-success/90 backdrop-blur-sm border border-success/20 shadow-lg">
								<CheckCircle2 className="w-4 h-4 text-white" />
								<span className="text-xs font-bold text-white uppercase tracking-wider">
									Completed
								</span>
							</div>
						) : isEnded ? (
							<div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-bg-tertiary/90 backdrop-blur-sm border border-white/10">
								<Clock className="w-4 h-4 text-text-tertiary" />
								<span className="text-xs font-bold text-text-tertiary uppercase tracking-wider">
									Expired
								</span>
							</div>
						) : null}
					</div>

					{/* Countdown Overlay (Bottom Right) */}
					{!isQuestCompleted &&
						!isEnded &&
						(countdownText || endDateFormatted) && (
							<div className="absolute bottom-4 right-4 z-20">
								<div className="flex items-center gap-2 px-3 py-2 rounded-full bg-black/70 backdrop-blur-sm border border-white/10 shadow-lg">
									<Clock className="w-4 h-4 text-amber-400" />
									<div className="flex items-center gap-2">
										{countdownText && (
											<span className="text-xs font-bold text-amber-300">
												{countdownText}
											</span>
										)}
										{endDateFormatted && (
											<span className="text-[10px] text-text-tertiary">
												Ends {endDateFormatted}
											</span>
										)}
									</div>
								</div>
							</div>
						)}
				</div>

				<div className="p-6 space-y-6 overflow-y-auto">
					<DialogHeader>
						<div className="flex items-start justify-between gap-4">
							<div className="space-y-1">
								<DialogTitle className="text-2xl font-bold text-white">
									{quest.name}
								</DialogTitle>
								{quest.startAt && (
									<div className="flex items-center gap-2 text-sm text-text-tertiary">
										<Calendar className="w-4 h-4" />
										<span>
											{new Date(quest.startAt).toLocaleDateString()}
											{quest.endAt &&
												` - ${new Date(quest.endAt).toLocaleDateString()}`}
										</span>
									</div>
								)}
							</div>
							<div className="flex flex-col items-end shrink-0">
								<div className="flex items-center gap-1.5 px-3 py-1 rounded-lg border border-brand/20 bg-brand/5">
									<Star className="w-4 h-4 fill-brand text-brand" />
									<span className="text-lg font-bold text-brand tabular-nums">
										{quest.points}
									</span>
									<span className="text-[10px] font-bold text-text-secondary uppercase">
										pts
									</span>
								</div>
							</div>
						</div>
					</DialogHeader>

					<div className="space-y-4">
						{/* Description */}
						<div className="space-y-2">
							<h3 className="text-sm font-medium text-text-secondary uppercase tracking-wider">
								Description
							</h3>
							<p className="text-sm text-text-default leading-relaxed">
								{quest.description ??
									"Complete this quest to earn rewards points."}
							</p>
						</div>

						{/* Progress Bar (if started) */}
						{hasProgress && !isQuestCompleted && (
							<div className="space-y-2 bg-bg-tertiary/30 p-4 rounded-xl border border-white/5">
								<div className="flex justify-between text-xs mb-1">
									<span className="text-text-secondary">Progress</span>
									<span className="text-brand font-medium">
										{progressPercent}%
									</span>
								</div>
								<div className="h-2 w-full bg-bg-tertiary rounded-full overflow-hidden">
									<div
										className="h-full bg-brand transition-all duration-500"
										style={{ width: `${progressPercent}%` }}
									/>
								</div>
							</div>
						)}

						{/* Steps */}
						<div className="space-y-3">
							<h3 className="text-sm font-medium text-text-secondary uppercase tracking-wider">
								Steps to Complete
							</h3>
							<div className="space-y-2">
								{steps.map((step, i) => {
									const isStepCompleted =
										hasProgress && progress
											? progress.completedStepIds
												? progress.completedStepIds.includes(step.id)
												: i < progress.completedSteps
											: false;

									return (
										<div
											key={step.id}
											className={cn(
												"flex items-start gap-3 p-3 rounded-lg border transition-all",
												isStepCompleted
													? "bg-success/5 border-success/20"
													: "bg-bg-tertiary/20 border-white/5",
											)}
										>
											<div
												className={cn(
													"mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0 border",
													isStepCompleted
														? "bg-success text-white border-success"
														: "bg-transparent text-text-tertiary border-white/10",
												)}
											>
												{isStepCompleted ? (
													<CheckCircle2 className="w-3.5 h-3.5" />
												) : (
													<span className="text-[10px] font-bold">{i + 1}</span>
												)}
											</div>
											<div className="flex-1 min-w-0">
												<p
													className={cn(
														"text-sm font-medium",
														isStepCompleted
															? "text-success/90 line-through decoration-success/50"
															: "text-text-default",
													)}
												>
													{step.name}
													{step.requiredCount > 1 &&
														` (${step.requiredCount}x)`}
												</p>
												{step.description && (
													<p className="text-xs text-text-tertiary mt-0.5">
														{step.description}
													</p>
												)}
											</div>
										</div>
									);
								})}
							</div>
						</div>
					</div>
				</div>

				<div className="p-6 border-t border-white/5 bg-bg-tertiary/10">
					{isQuestCompleted ? (
						<Button
							className="w-full gap-2 bg-success/10 text-success hover:bg-success/20 border border-success/20"
							variant="tertiary"
							disabled
						>
							<CheckCircle2 className="w-4 h-4" />
							Quest Completed
						</Button>
					) : isEnded ? (
						<Button className="w-full" variant="outline" disabled>
							Quest Expired
						</Button>
					) : canProgressNow === false ? (
						<Button className="w-full gap-2" variant="outline" disabled>
							<Clock className="w-4 h-4" />
							Coming Soon
						</Button>
					) : (
						<a
							href="https://web.shinkai.com"
							target="_blank"
							rel="noreferrer"
							onClick={onCtaClick}
							className={cn(
								buttonVariants({ variant: "default", size: "lg" }),
								"w-full gap-2 font-bold",
							)}
						>
							{shouldShowRewardsGate
								? "Unlock Rewards"
								: hasProgress && (progress?.completedSteps ?? 0) > 0
									? "Resume Challenge"
									: "Start Challenge"}
						</a>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
}
